/**
 *  @class
 *  @function PromotionalPopup
 *
 *  Sticky promotional pop-up shown below the header.
 *
 *  Display only — it reads the cart subtotal (via /cart.js) but never mutates
 *  the cart. The actual add-to-cart is handled elsewhere; this element only
 *  reacts to cart changes and decides whether/what to show.
 *
 *  Trigger model
 *  -------------
 *  The pop-up is evaluated on: initial load (optional), every cart change and
 *  whenever the cart drawer closes. It only appears while the cart drawer is
 *  closed. The active tier is the next reward the customer is working towards
 *  (the lowest threshold still above the subtotal); [amount] is the gap to it.
 *
 *  A message is shown when the customer:
 *    - starts targeting a different tier than last tracked, once they have at
 *      least one item in the cart, regardless of the "almost there" distance
 *      (covers both adding the first product and crossing into a higher
 *      tier), or
 *    - moves within the "almost there" distance of the next tier.
 *  Both are bounded to at most once per tier band by their own state, so both
 *  always bypass the frequency rules below — without that, entering a tier
 *  would start a cooldown that could block its own near-nudge moments later.
 *  It is NOT re-shown on every cart update while the customer stays in the
 *  same tier band.
 *
 *  Frequency rules (optional): cooldown between pop-ups, a maximum per
 *  rolling session window. Currently inert for tier/near messages (see
 *  above) — only the page-load "initial" message respects them, and that's
 *  already capped to once per session on its own.
 */
if (!customElements.get('promotional-popup')) {
  const STORAGE_KEY = 'zusss:promoPopup';

  class PromotionalPopup extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.config = this.parseConfig();
      this.tiers = this.buildTiers(this.config.tiers);

      if (!this.tiers.length) {
        return;
      }

      // Settings from data-attributes.
      this.mode = this.dataset.mode || 'tiered';
      this.nearThresholdCents = (parseFloat(this.dataset.nearThreshold) || 0) * 100;
      this.showOnLoad = this.dataset.showOnLoad === 'true';
      this.initialDelayMs = (parseFloat(this.dataset.initialDelay) || 0) * 1000;
      this.initialDurationMs = (parseFloat(this.dataset.initialDuration) || 0) * 1000;
      this.finalDurationMs = (parseFloat(this.dataset.finalDuration) || 0) * 1000;
      this.initialMessage = this.config.initialMessage || '';
      this.initialImage = this.config.initialImage || '';
      this.finalMessage = this.config.finalMessage || '';
      this.frequencyEnabled = this.dataset.frequencyEnable === 'true';
      this.cooldownMs = (parseFloat(this.dataset.cooldownMinutes) || 0) * 60 * 1000;
      this.maxPerSession = parseInt(this.dataset.maxPerSession, 10) || 0;
      this.sessionMs = (parseFloat(this.dataset.sessionMinutes) || 30) * 60 * 1000;

      this.textEl = this.querySelector('.promo-popup__text');
      this.mediaEl = this.querySelector('.promo-popup__media');
      this.closeEl = this.querySelector('.promo-popup__close');
      this.hideTimer = null;
      this.pending = false;

      this.state = this.loadState();

      // Hidden attribute prevents a flash before JS runs; visibility is now
      // handled with the .is-visible class so transitions work.
      this.removeAttribute('hidden');

      this.bindEvents();

      if (this.showOnLoad && !this.isDrawerOpen()) {
        if (this.initialDelayMs > 0) {
          setTimeout(() => this.evaluate('load'), this.initialDelayMs);
        } else {
          this.evaluate('load');
        }
      }
    }

    /* ----------------------------------------------------------------- config */

    parseConfig() {
      const script = this.querySelector('.promo-popup__config');
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return { tiers: [] };
      }
    }

    // Normalise tiers: drop the ones without a usable threshold and sort
    // ascending so "next tier" lookups are simple.
    buildTiers(tiers) {
      return (tiers || [])
        .map((tier) => ({
          index: tier.index,
          thresholdCents: Math.round((parseFloat(tier.threshold) || 0) * 100),
          copy: tier.copy || '',
          productTitle: tier.productTitle || '',
          image: tier.image || '',
          durationMs: (parseFloat(tier.duration) || 0) * 1000
        }))
        .filter((tier) => tier.thresholdCents > 0)
        .sort((a, b) => a.thresholdCents - b.thresholdCents);
    }

    /* ------------------------------------------------------------------ events */

    bindEvents() {
      this.closeEl.addEventListener('click', this.onClose.bind(this));

      // Cart changes coming from the theme (and any compatible integration).
      ['line-item:change:end', 'ajaxCart:updated', 'cart:refresh', 'cart:updated'].forEach((name) => {
        document.addEventListener(name, () => this.onCartChange());
      });

      // Hide while the drawer is open; re-evaluate once it closes.
      document.addEventListener('cart-drawer:open', () => this.hide());

      this.drawerObserver = new MutationObserver(() => {
        const open = this.isDrawerOpen();
        if (this.drawerWasOpen && !open) {
          // Drawer just closed.
          this.evaluate('drawer-close');
        }
        this.drawerWasOpen = open;
      });
      this.drawerWasOpen = this.isDrawerOpen();
      this.drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    onCartChange() {
      // While the drawer is open we wait for it to close before showing anything.
      if (this.isDrawerOpen()) {
        this.pending = true;
        return;
      }
      this.evaluate('cart-change');
    }

    onClose() {
      this.hide();
    }

    isDrawerOpen() {
      return document.body.classList.contains('open-cart');
    }

    /* ---------------------------------------------------------------- evaluate */

    async evaluate(reason) {
      if (this.evaluating) {
        return;
      }
      this.evaluating = true;
      try {
        if (this.isDrawerOpen() && reason !== 'drawer-close') {
          this.pending = true;
          return;
        }
        this.pending = false;

        const cartState = await this.getCartState();
        if (cartState === null) {
          return;
        }
        const { subtotal, itemCount } = cartState;

        const active = this.getActiveTier(subtotal);
        const observedIndex = active ? active.index : null;

        // All rewards reached (no next tier to nudge towards).
        if (!active) {
          if (this.finalMessage && !this.state.finalShown) {
            this.render(null, 0, this.finalMessage, '');
            this.show(this.finalDurationMs);
            this.recordShow(null, 'final', false);
          } else {
            this.hide();
          }
          this.commitObserved(null);
          return;
        }

        const remaining = Math.max(0, active.tier.thresholdCents - subtotal);

        // A tier only nudges when the customer is within the "almost there"
        // distance BELOW that tier's own threshold. Crossing a lower tier does
        // NOT surface a higher tier's nudge until the customer is actually close
        // to it — e.g. with a €100 tier and a €10 distance, €79.92 stays hidden
        // (€20.08 away) and only €90+ triggers it.
        const isNear = this.nearThresholdCents > 0 && remaining <= this.nearThresholdCents;

        // The "before the customer adds anything" message: empty cart only,
        // shown at most once per session.
        const isInitial = this.showOnLoad && !this.state.initialShown && subtotal === 0;

        // Once the customer has items in the cart, targeting a different tier
        // than last tracked always nudges with that tier's message — even far
        // from its "almost there" distance. This covers both adding the first
        // product (Tier 1 becomes active) and crossing into a higher tier
        // (e.g. Tier 1 -> Tier 2).
        const tierChanged = observedIndex !== this.state.lastTierIndex;
        const isTierEntered = itemCount > 0 && tierChanged;

        // A tier's nudge is shown once while the customer stays within its
        // window; it re-arms only when they move to a different tier band.
        const alreadyShownThisTier = this.state.nearShownForTier === observedIndex;

        let trigger = null;
        if (isTierEntered) {
          trigger = 'tier-entered';
        } else if (isNear && !alreadyShownThisTier) {
          trigger = 'near';
        } else if (isInitial) {
          trigger = 'initial';
        }

        if (!trigger) {
          // Nothing to show, but remember the band so its per-band flags reset
          // cleanly when the customer later moves to a different tier.
          this.commitObserved(observedIndex);
          return;
        }

        // Entering a new tier bypasses the cooldown; re-nudges within the same
        // band respect it.
        if (!this.passesFrequencyRules(tierChanged ? 'tier-up' : trigger)) {
          this.commitObserved(observedIndex);
          return;
        }

        const overrideCopy = trigger === 'initial' ? this.initialMessage : null;
        const overrideImage = trigger === 'initial' ? this.initialImage : undefined;
        this.render(active.tier, remaining, overrideCopy, overrideImage);
        this.show(trigger === 'initial' ? this.initialDurationMs : active.tier.durationMs);
        this.recordShow(observedIndex, trigger, isNear);
      } finally {
        this.evaluating = false;
        if (this.pending && !this.isDrawerOpen()) {
          this.pending = false;
          this.evaluate('pending');
        }
      }
    }

    // Returns the next reward the customer is working towards (the lowest
    // threshold still above the subtotal), or null when all rewards are reached.
    // The tiers array already reflects the mode (single mode only contains
    // Tier 1), so the same lookup works for both modes.
    getActiveTier(subtotal) {
      const next = this.tiers.find((tier) => tier.thresholdCents > subtotal);
      return next ? { tier: next, index: next.index } : null;
    }

    /* -------------------------------------------------------------- frequency */

    passesFrequencyRules(trigger) {
      if (!this.frequencyEnabled) {
        return true;
      }

      // Tier transitions and "almost there" nudges are each bounded to at
      // most once per tier band by their own per-band dedup logic, so neither
      // is the repeated nagging these rules exist to throttle. Without this,
      // a tier-entered show would start a cooldown that could then block the
      // (legitimate, distinct) near-nudge for that same tier moments later.
      if (trigger === 'tier-up' || trigger === 'near') {
        return true;
      }

      const now = Date.now();
      // Keep only the shows inside the rolling session window.
      this.state.timestamps = (this.state.timestamps || []).filter((t) => now - t < this.sessionMs);

      if (this.maxPerSession > 0 && this.state.timestamps.length >= this.maxPerSession) {
        return false;
      }

      if (this.cooldownMs > 0 && this.state.lastShown) {
        if (now - this.state.lastShown < this.cooldownMs) {
          return false;
        }
      }
      return true;
    }

    recordShow(tierIndex, trigger, isNear) {
      const now = Date.now();
      this.state.timestamps = this.state.timestamps || [];
      this.state.timestamps.push(now);
      this.state.lastShown = now;
      if (trigger === 'initial') {
        // Deliberately doesn't update lastTierIndex: the initial message can
        // fire before the customer has any items, and leaving lastTierIndex
        // untouched lets the next real cart addition still count as
        // "entering" that tier.
        this.state.initialShown = true;
      } else if (trigger === 'final') {
        this.state.finalShown = true;
      } else {
        this.state.lastTierIndex = tierIndex;
      }
      if (isNear) {
        this.state.nearShownForTier = tierIndex;
      }
      this.state.updatedAt = now;
      this.saveState();
    }

    // Record the currently observed target band without showing anything. When
    // the band changes, the per-band flags are reset so the new band can nudge
    // again.
    commitObserved(tierIndex) {
      if (this.state.lastTierIndex === tierIndex) {
        return;
      }
      this.state.lastTierIndex = tierIndex;
      this.state.nearShownForTier = null;
      this.state.updatedAt = Date.now();
      this.saveState();
    }

    /* ----------------------------------------------------------------- render */

    render(tier, remainingCents, overrideCopy, overrideImage) {
      const format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '€{{amount_with_comma_separator}}';
      let amount = remainingCents;
      if (typeof formatMoney === 'function') {
        amount = formatMoney(remainingCents, format);
      }

      const copy = (overrideCopy || (tier ? tier.copy : '') || '')
        .split('[amount]').join('<span class="promo-popup__amount">' + amount + '</span>')
        .split('[product_title]').join((tier ? tier.productTitle : '') || '');

      this.textEl.innerHTML = copy;

      // overrideImage is `undefined` for non-initial triggers (fall back to
      // the tier's own image) and a possibly-empty string for the initial
      // trigger (its own image, or none when left blank). tier may be null
      // when rendering the final message (no active tier).
      const image = overrideImage !== undefined ? overrideImage : (tier ? tier.image : '');
      if (image) {
        this.mediaEl.innerHTML = '<img src="' + image + '" alt="" width="56" height="56" loading="lazy">';
        this.mediaEl.hidden = false;
      } else {
        this.mediaEl.innerHTML = '';
        this.mediaEl.hidden = true;
      }
    }

    show(durationMs) {
      clearTimeout(this.hideTimer);
      // Force a reflow so re-showing restarts the entry transition.
      void this.offsetWidth;
      this.classList.add('is-visible');

      if (durationMs && durationMs > 0) {
        this.hideTimer = setTimeout(() => this.hide(), durationMs);
      }
    }

    hide() {
      clearTimeout(this.hideTimer);
      this.classList.remove('is-visible');
    }

    /* ---------------------------------------------------------------- cart api */

    async getCartState() {
      // Locale-aware cart endpoint, e.g. /cart.js or /en/cart.js.
      const cartUrl = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      try {
        const response = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
          return null;
        }
        const cart = await response.json();
        // total_price is after discounts — the amount the customer pays.
        if (typeof cart.total_price !== 'number') {
          return null;
        }
        return { subtotal: cart.total_price, itemCount: cart.item_count || 0 };
      } catch (e) {
        return null;
      }
    }

    /* ------------------------------------------------------------------ state */

    loadState() {
      let state;
      try {
        state = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {
        state = {};
      }

      const now = Date.now();
      state.timestamps = (state.timestamps || []).filter((t) => now - t < this.sessionMs);

      // A gap longer than the session window starts a fresh session, so the
      // initial / tier messages may show again.
      if (!state.updatedAt || now - state.updatedAt > this.sessionMs) {
        state.lastTierIndex = null;
        state.nearShownForTier = null;
        state.initialShown = false;
        state.finalShown = false;
      }

      return {
        lastTierIndex: state.lastTierIndex != null ? state.lastTierIndex : null,
        nearShownForTier: state.nearShownForTier != null ? state.nearShownForTier : null,
        initialShown: !!state.initialShown,
        finalShown: !!state.finalShown,
        timestamps: state.timestamps,
        lastShown: state.lastShown || 0,
        updatedAt: state.updatedAt || 0
      };
    }

    saveState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (e) {
        /* storage unavailable — fail silently */
      }
    }
  }

  customElements.define('promotional-popup', PromotionalPopup);
}
